import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_URL } from '../../../api/api';

const API_EMPLOYEE = `${API_URL}/employees`;

const ManageEmployeeLayer = () => {
    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('id');
    const [sortDirection, setSortDirection] = useState('asc');

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API_EMPLOYEE);
            setEmployees(res.data.data || []);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load employees',
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    const deleteEmployee = (id, name) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>Employee <strong>${name}</strong> will be permanently deleted.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    await axios.delete(`${API_EMPLOYEE}/${id}`);
                    return { success: true };
                } catch (error) {
                    throw new Error('Failed to delete employee');
                }
            }
        }).then((result) => {
            if (result.isConfirmed && result.value?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Employee has been deleted.',
                    timer: 1500,
                    showConfirmButton: false
                });
                fetchEmployees();
            }
        });
    };

    const toggleStatus = async (employee) => {
        const updatedStatus = employee.status === true || employee.status === 1 ? 0 : 1;
        const action = updatedStatus ? 'activate' : 'deactivate';

        try {
            await axios.put(`${API_EMPLOYEE}/${employee.id}/status`, {
                status: updatedStatus,
            });

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: `Employee ${action}d successfully.`,
                timer: 1500,
                showConfirmButton: false
            });
            fetchEmployees();
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update status',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    // Filter and search functionality
    const filteredEmployees = employees.filter(emp => {
        const searchLower = searchTerm.toLowerCase();
        return (
            emp.firstname?.toLowerCase().includes(searchLower) ||
            emp.lastname?.toLowerCase().includes(searchLower) ||
            emp.loginusername?.toLowerCase().includes(searchLower) ||
            emp.designation?.toLowerCase().includes(searchLower) ||
            emp.branch?.branchname?.toLowerCase().includes(searchLower)
        );
    });

    // Sorting functionality
    const sortedEmployees = [...filteredEmployees].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEmployees = sortedEmployees.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedEmployees.length / itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <i className="ri-expand-vertical-line ms-1 opacity-50"></i>;
        return sortDirection === 'asc'
            ? <i className="ri-sort-asc ms-1"></i>
            : <i className="ri-sort-desc ms-1"></i>;
    };

    if (loading && employees.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading employees...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0">
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <h5 className="mb-0 fw-semibold">Employee Management</h5>
                            <p className="text-muted mb-0">
                                {employees.length} employee{employees.length !== 1 ? 's' : ''} found
                            </p>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex gap-2 justify-content-md-end">
                                <div className="input-group" style={{ maxWidth: '300px' }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="ri-search-line"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search employees..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                <Link
                                    to="/add-employee"
                                    className="btn btn-primary d-flex align-items-center gap-2"
                                >
                                    <i className="ri-user-add-line"></i>
                                    <span className="d-none d-md-inline">Add Employee</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-body p-0">
                    {currentEmployees.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="py-5">
                                <i className="ri-user-search-line display-1 text-muted"></i>
                                <h5 className="mt-3">No employees found</h5>
                                <p className="text-muted">
                                    {searchTerm ? 'Try adjusting your search terms' : 'Add your first employee to get started'}
                                </p>
                                {!searchTerm && (
                                    <Link to="/add-employee" className="btn btn-primary mt-2">
                                        Add Employee
                                    </Link>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th
                                                style={{ cursor: 'pointer', minWidth: '80px' }}
                                                onClick={() => handleSort('id')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    ID
                                                    <SortIcon field="id" />
                                                </div>
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', minWidth: '150px' }}
                                                onClick={() => handleSort('designation')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    Designation
                                                    <SortIcon field="designation" />
                                                </div>
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', minWidth: '180px' }}
                                                onClick={() => handleSort('firstname')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    Name
                                                    <SortIcon field="firstname" />
                                                </div>
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', minWidth: '120px' }}
                                                onClick={() => handleSort('loginusername')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    Username
                                                    <SortIcon field="loginusername" />
                                                </div>
                                            </th>
                                            <th style={{ minWidth: '150px' }}>Roles</th>
                                            <th
                                                style={{ cursor: 'pointer', minWidth: '120px' }}
                                                onClick={() => handleSort('branch.branchname')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    Branch
                                                    <SortIcon field="branch.branchname" />
                                                </div>
                                            </th>
                                            <th style={{ minWidth: '100px' }}>Status</th>
                                            <th style={{ minWidth: '120px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentEmployees.map((emp) => (
                                            <tr key={emp.id} className="align-middle">
                                                <td>
                                                    <span className="badge bg-light text-dark border">
                                                        #{emp.id}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-truncate d-inline-block" style={{ maxWidth: '140px' }}>
                                                        {emp.designation || '-'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>{emp.firstname} {emp.lastname}</strong>
                                                    </div>
                                                    <small className="text-muted">{emp.email || 'No email'}</small>
                                                </td>
                                                <td>
                                                    <code>{emp.loginusername}</code>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {emp.roles && emp.roles.length > 0 ? (
                                                            emp.roles.slice(0, 2).map((role, index) => (
                                                                <span key={index} className="badge bg-info-subtle text-info">
                                                                    {role}
                                                                </span>
                                                            ))
                                                        ) : '-'}
                                                        {emp.roles && emp.roles.length > 2 && (
                                                            <span className="badge bg-secondary">
                                                                +{emp.roles.length - 2}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <i className="ri-building-2-line me-2 text-muted"></i>
                                                        {emp.branch?.branchname || '-'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => toggleStatus(emp)}
                                                        className={`btn btn-sm ${emp.status === 1 || emp.status === true
                                                            ? 'btn-success'
                                                            : 'btn-outline-secondary'} rounded-pill px-3`}
                                                        style={{ minWidth: '90px' }}
                                                    >
                                                        {emp.status === 1 || emp.status === true ? (
                                                            <>
                                                                <i className="ri-check-line me-1"></i>
                                                                Active
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-close-line me-1"></i>
                                                                Inactive
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Link
                                                            to={`/add-employee/${emp.id}`}
                                                            className="btn btn-sm btn-outline-primary d-flex align-items-center"
                                                            title="Edit"
                                                        >
                                                            <i className="ri-pencil-line"></i>
                                                            <span className="ms-1 d-none d-md-inline">Edit</span>
                                                        </Link>
                                                        <button
                                                            onClick={() => deleteEmployee(emp.id, `${emp.firstname} ${emp.lastname}`)}
                                                            className="btn btn-sm btn-outline-danger d-flex align-items-center"
                                                            title="Delete"
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                            <span className="ms-1 d-none d-md-inline">Delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="border-top">
                                    <div className="d-flex justify-content-between align-items-center p-3">
                                        <small className="text-muted">
                                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedEmployees.length)} of {sortedEmployees.length} entries
                                        </small>
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(currentPage - 1)}
                                                    >
                                                        <i className="ri-arrow-left-s-line"></i>
                                                    </button>
                                                </li>

                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let pageNum;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }

                                                    return (
                                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </li>
                                                    );
                                                })}

                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(currentPage + 1)}
                                                    >
                                                        <i className="ri-arrow-right-s-line"></i>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageEmployeeLayer;